import { Button, Group, Modal, Stack, Text, ThemeIcon, useMantineTheme } from '@mantine/core';
import { AlertCircle } from 'lucide-react';
import React from 'react';

/**
 * Reusable Confirmation Modal Component
 * Replaces window.confirm() calls with a modal dialog
 */
interface ConfirmationModalProps {
  cancelText?: string;
  children: React.ReactNode;
  /** A literal CSS color (not a Mantine theme token) — the `filled` button background is set via
   * inline `style`, not Mantine's `color` prop, so it stays solid even if the host shell's CSS
   * doesn't let Mantine's own color variables resolve. */
  confirmColor?: string;
  // Shows a loader inside the confirm button (and disables both buttons) while an async
  // confirm action is in flight.
  confirmLoading?: boolean;
  confirmText?: string;
  onCancel: () => void;
  onConfirm: () => void;
  opened: boolean;
  title: string;
  zIndex?: number;
}

export const ConfirmationModal = ({
  cancelText = 'Cancel',
  children,
  confirmColor = '#e03131',
  confirmLoading = false,
  confirmText = 'Confirm',
  onCancel,
  onConfirm,
  opened,
  title,
  zIndex,
}: ConfirmationModalProps) => {
  const theme = useMantineTheme();

  return (
    <Modal
      centered
      lockScroll={false}
      onClose={onCancel}
      opened={opened}
      styles={{
        title: {
          alignItems: 'center',
          display: 'flex',
          fontWeight: 600,
          gap: theme.spacing.xs,
        },
      }}
      title={
        <>
          {/* `filled` (solid background, not a low-alpha tint) + a literal hex rather than a
              theme color name so this renders as a solid, vivid red regardless of whether the
              host shell's CSS lets Mantine's own color variables resolve correctly. */}
          <ThemeIcon color="#e03131" radius="xl" size={36} variant="filled">
            <AlertCircle color="#fff" size={22} />
          </ThemeIcon>{' '}
          {title}
        </>
      }
      zIndex={zIndex}
    >
      <Stack gap="md">
        <Group gap="sm">
          <Text>{children}</Text>
        </Group>
        <Group gap="sm" justify="flex-end">
          {/* `outline`'s border/text normally read from Mantine's primary-color CSS variables —
              pinned to a literal hex here too (see the delete icon's own comment above) so it
              doesn't wash out if those variables don't resolve inside the host shell. */}
          <Button
            disabled={confirmLoading}
            onClick={onCancel}
            style={{ borderColor: '#495057', color: '#495057' }}
            variant="outline"
          >
            {cancelText}
          </Button>
          <Button
            disabled={confirmLoading}
            loading={confirmLoading}
            onClick={onConfirm}
            style={{ backgroundColor: confirmColor, color: '#fff' }}
            variant="filled"
          >
            {confirmText}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
